/**
 * Client (organization) CSV import — column spec, header mapping, normalization,
 * and duplicate detection. Shared by the admin import modal and the server route.
 */

export const CLIENT_IMPORT_MAX_ROWS = 500;

export type ClientImportColumnKey =
  | 'name'
  | 'org_code'
  | 'contact_name'
  | 'contact_email'
  | 'notes'
  | 'billing_email'
  | 'billing_street'
  | 'billing_city'
  | 'billing_state'
  | 'billing_zip'
  | 'billing_country'
  | 'plan_name';

export interface ClientImportColumnSpec {
  key: ClientImportColumnKey;
  header: string;
  aliases: string[];
  description: string;
  example: string;
}

export const CLIENT_IMPORT_COLUMNS: ClientImportColumnSpec[] = [
  { key: 'name', header: 'name', aliases: ['client', 'client_name', 'organization', 'org', 'org_name', 'company', 'company_name'], description: 'Required. Organization name. Must not already exist.', example: 'AutoZone' },
  { key: 'org_code', header: 'org_code', aliases: ['code', 'client_code', 'organization_code'], description: 'Optional 6-digit code. Leave blank to auto-generate.', example: '' },
  { key: 'contact_name', header: 'contact_name', aliases: ['contact', 'primary_contact', 'contact_person'], description: 'Primary contact name.', example: 'Jane Doe' },
  { key: 'contact_email', header: 'contact_email', aliases: ['email', 'contact_email_address'], description: 'Primary contact email.', example: 'jane@autozone.com' },
  { key: 'notes', header: 'notes', aliases: ['description', 'comments'], description: 'Free text.', example: 'National retail account' },
  { key: 'billing_email', header: 'billing_email', aliases: ['ap_email', 'invoice_email'], description: 'Where invoices go.', example: 'ap@autozone.com' },
  { key: 'billing_street', header: 'billing_street', aliases: ['street', 'address', 'billing_address', 'address1'], description: 'Billing street address.', example: '123 S Front St' },
  { key: 'billing_city', header: 'billing_city', aliases: ['city'], description: 'Billing city.', example: 'Memphis' },
  { key: 'billing_state', header: 'billing_state', aliases: ['state', 'st', 'province'], description: 'Billing state.', example: 'TN' },
  { key: 'billing_zip', header: 'billing_zip', aliases: ['zip', 'zip_code', 'postal_code', 'postcode'], description: 'Billing ZIP / postal code.', example: '38103' },
  { key: 'billing_country', header: 'billing_country', aliases: ['country'], description: 'Billing country.', example: 'USA' },
  { key: 'plan_name', header: 'plan_name', aliases: ['plan', 'tier', 'package'], description: 'Plan or package label.', example: '' },
];

export type ClientImportRow = Partial<Record<ClientImportColumnKey, string>>;

export interface NormalizedClientImportRow {
  rowNumber: number;
  name: string;
  slug: string;
  /** Normalized key for exact duplicate detection: lowercase, alphanumerics only. */
  dedupeKey: string;
  /** Looser key with common company suffixes removed, for "similar name" warnings. */
  similarKey: string;
  orgCode?: string;
  contactName?: string;
  contactEmail?: string;
  notes?: string;
  billingEmail?: string;
  billingStreet?: string;
  billingCity?: string;
  billingState?: string;
  billingZip?: string;
  billingCountry?: string;
  planName?: string;
  errors: string[];
  warnings: string[];
}

export interface ClientImportRowResult {
  rowNumber: number;
  ok: boolean;
  clientId?: string;
  name: string;
  orgCode?: string;
  errors: string[];
  warnings: string[];
}

export interface ClientImportResponse {
  dryRun: boolean;
  total: number;
  created: number;
  failed: number;
  results: ClientImportRowResult[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function stripBom(value: string): string {
  return value.charCodeAt(0) === 0xfeff ? value.slice(1) : value;
}

export function normalizeClientHeader(header: string): string {
  return stripBom(header).trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

export function mapClientImportHeaders(headers: string[]): {
  mapping: Array<ClientImportColumnKey | null>;
  unknown: string[];
  hasName: boolean;
} {
  const lookup = new Map<string, ClientImportColumnKey>();
  for (const col of CLIENT_IMPORT_COLUMNS) {
    lookup.set(col.header, col.key);
    for (const a of col.aliases) lookup.set(a, col.key);
  }
  const mapping: Array<ClientImportColumnKey | null> = [];
  const unknown: string[] = [];
  const seen = new Set<ClientImportColumnKey>();
  for (const h of headers) {
    const key = lookup.get(normalizeClientHeader(h)) ?? null;
    if (key && !seen.has(key)) { mapping.push(key); seen.add(key); }
    else { mapping.push(null); if (h.trim()) unknown.push(h.trim()); }
  }
  return { mapping, unknown, hasName: seen.has('name') };
}

export function clientImportRowsFromTable(mapping: Array<ClientImportColumnKey | null>, records: string[][]): ClientImportRow[] {
  const rows: ClientImportRow[] = [];
  for (const record of records) {
    const row: ClientImportRow = {};
    let hasValue = false;
    record.forEach((cell, i) => {
      const key = mapping[i];
      if (!key) return;
      const value = (cell ?? '').trim();
      if (value) { row[key] = value; hasValue = true; }
    });
    if (hasValue) rows.push(row);
  }
  return rows;
}

/** Same slug rule the Clients page uses when creating a client by hand. */
export function slugifyClientName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

/** Exact-duplicate key: case, spacing, and punctuation insensitive. */
export function clientDedupeKey(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

const COMPANY_SUFFIXES = /\b(llc|l\.l\.c\.|inc|inc\.|incorporated|corp|corp\.|corporation|co|co\.|company|ltd|ltd\.|limited|plc|lp|llp|group|holdings|the)\b/g;

/** Looser key for "looks similar" warnings: also drops common company suffixes. */
export function clientSimilarKey(name: string): string {
  return name.toLowerCase().replace(COMPANY_SUFFIXES, ' ').replace(/[^a-z0-9]+/g, '');
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function clip(value: string | undefined, max: number): string | undefined {
  if (!value) return undefined;
  const v = value.trim();
  return v.length > max ? v.slice(0, max) : v;
}

export function normalizeClientImportRow(row: ClientImportRow, rowNumber: number): NormalizedClientImportRow {
  const errors: string[] = [];
  const warnings: string[] = [];

  const name = clip(row.name, 200) || '';
  if (!name) errors.push('name is required');

  const orgCode = clip(row.org_code, 20);
  if (orgCode && !/^\d{6,7}$/.test(orgCode)) errors.push(`org_code must be 6 or 7 digits (got "${orgCode}")`);

  const contactEmail = clip(row.contact_email, 200);
  if (contactEmail && !EMAIL_RE.test(contactEmail)) errors.push(`contact_email is not a valid email: "${contactEmail}"`);
  const billingEmail = clip(row.billing_email, 200);
  if (billingEmail && !EMAIL_RE.test(billingEmail)) errors.push(`billing_email is not a valid email: "${billingEmail}"`);

  const slug = slugifyClientName(name);
  if (name && !slug) errors.push('name must contain at least one letter or number');

  return {
    rowNumber,
    name,
    slug,
    dedupeKey: clientDedupeKey(name),
    similarKey: clientSimilarKey(name),
    orgCode,
    contactName: clip(row.contact_name, 200),
    contactEmail,
    notes: clip(row.notes, 5000),
    billingEmail,
    billingStreet: clip(row.billing_street, 300),
    billingCity: clip(row.billing_city, 100),
    billingState: clip(row.billing_state, 50),
    billingZip: clip(row.billing_zip, 20),
    billingCountry: clip(row.billing_country, 100),
    planName: clip(row.plan_name, 100),
    errors,
    warnings,
  };
}

/**
 * Flag duplicates inside one batch. The first occurrence stays valid; later
 * rows with the same normalized name, slug, or org code get an error.
 */
export function markIntraBatchDuplicates(rows: NormalizedClientImportRow[]): void {
  const seenKey = new Map<string, number>();
  const seenSlug = new Map<string, number>();
  const seenCode = new Map<string, number>();
  for (const r of rows) {
    if (!r.name) continue;
    const k = seenKey.get(r.dedupeKey);
    if (k !== undefined) r.errors.push(`Duplicate of row ${k} ("${r.name}") in this file`);
    else seenKey.set(r.dedupeKey, r.rowNumber);
    if (r.slug) {
      const s = seenSlug.get(r.slug);
      if (s !== undefined && s !== k) r.errors.push(`Same slug as row ${s} ("${r.slug}")`);
      else if (s === undefined) seenSlug.set(r.slug, r.rowNumber);
    }
    if (r.orgCode) {
      const c = seenCode.get(r.orgCode);
      if (c !== undefined) r.errors.push(`org_code ${r.orgCode} is also used on row ${c}`);
      else seenCode.set(r.orgCode, r.rowNumber);
    }
  }
}

function csvEscape(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function buildClientImportTemplate(): string {
  const header = CLIENT_IMPORT_COLUMNS.map(c => c.header).join(',');
  const example = CLIENT_IMPORT_COLUMNS.map(c => csvEscape(c.example)).join(',');
  const second = CLIENT_IMPORT_COLUMNS.map(c => {
    switch (c.key) {
      case 'name': return 'Type Six';
      case 'contact_name': return 'Sam Lee';
      case 'contact_email': return 'sam@typesix.com';
      case 'billing_city': return 'Sunrise';
      case 'billing_state': return 'FL';
      case 'billing_country': return 'USA';
      default: return '';
    }
  }).join(',');
  return `${header}\r\n${example}\r\n${second}\r\n`;
}
